/**
 * Session cart for multi-facility checkout (Hub).
 * Cap includes already-active facilities on the account.
 */

export const SELF_SERVE_FACILITY_CAP = 15;
export const PREFERRED_FACILITIES_CART_KEY = "preferred_facilities_for_pricing";
/** Legacy single-facility key — cleared on multi-cart write so Pricing is not hijacked. */
export const PREFERRED_FACILITY_LEGACY_KEY = "preferred_facility_for_pricing";

export type CartFacilityItem = {
  id?: number | null;
  user_id?: number | string;
  is_verified?: boolean;
  is_active?: boolean;
  facility_name: string;
  facility_type?: string;
  facility_type_id?: number | null;
  discount_coupon_name?: string | null;
  discount_end_date?: string | null;
  ccn_number?: string;
  city?: string;
  state?: string;
  country?: string;
  phone_number?: string;
  vertical?: string;
  primary_profile_slug?: string;
  facility_details?: Record<string, unknown>;
  /** Stable client key when UPF id not yet known */
  cart_key: string;
  /** Plan family hint for order summary grouping */
  plan_family?: "pro" | "rhc";
};

export function isRhcOrFqhcType(facilityType?: string | null): boolean {
  const t = (facilityType || "").toLowerCase();
  return (
    t.includes("rural health") ||
    t.includes("rhc") ||
    t.includes("federally qualified") ||
    t.includes("fqhc")
  );
}

export function planFamilyForFacilityType(
  facilityType?: string | null,
): "pro" | "rhc" {
  return isRhcOrFqhcType(facilityType) ? "rhc" : "pro";
}

/** Cap from trial-facility-gate: locked trial → 1; unlocked → SELF_SERVE_FACILITY_CAP. */
export function effectiveSelfServeCartCap(unlockedMulti: boolean): number {
  return unlockedMulti ? SELF_SERVE_FACILITY_CAP : 1;
}

/**
 * @param unlockedMulti From GET /billing/trial-facility-gate.
 *   false = active trial: allow exactly 1 facility in cart (do not subtract
 *   existingActiveCount — that zeroed Add for trial users who already have a plan).
 *   true = multi: SELF_SERVE_FACILITY_CAP minus actives minus cart.
 *   Omit → infer unlock from existingActiveCount > 0 (legacy callers).
 */
export function remainingCartSlots(
  existingActiveCount: number,
  cartLength: number,
  unlockedMulti?: boolean,
): number {
  const unlocked =
    typeof unlockedMulti === "boolean"
      ? unlockedMulti
      : existingActiveCount > 0;
  if (!unlocked) {
    return Math.max(0, 1 - cartLength);
  }
  return Math.max(
    0,
    SELF_SERVE_FACILITY_CAP - existingActiveCount - cartLength,
  );
}

/** Sales bypass adds any facility. Active count and cart length do not apply the 15 cap. */
export function isSelfServeCartFull(
  existingActiveCount: number,
  cartLength: number,
  unlockedMulti?: boolean,
  ignoreCap = false,
): boolean {
  if (ignoreCap) return false;
  return (
    remainingCartSlots(existingActiveCount, cartLength, unlockedMulti) <= 0
  );
}

export function readFacilityCart(): CartFacilityItem[] {
  try {
    const raw = sessionStorage.getItem(PREFERRED_FACILITIES_CART_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? (parsed as CartFacilityItem[]) : [];
  } catch {
    return [];
  }
}

export function writeFacilityCart(items: CartFacilityItem[]): void {
  try {
    sessionStorage.setItem(PREFERRED_FACILITIES_CART_KEY, JSON.stringify(items));
    // Never seed preferred_facility_for_pricing from the multi cart.
    // That legacy key drives Select Plan / Dashboard preferred→pricing hijack.
    // Single-facility Pricing writers set it themselves when intentionally opening Select Plan.
    sessionStorage.removeItem(PREFERRED_FACILITY_LEGACY_KEY);
  } catch {
    // ignore quota / private mode
  }
}

export function clearFacilityCart(): void {
  try {
    sessionStorage.removeItem(PREFERRED_FACILITIES_CART_KEY);
    sessionStorage.removeItem(PREFERRED_FACILITY_LEGACY_KEY);
  } catch {
    // ignore
  }
}

export function facilityCartKeyFromDetails(details: {
  ccn_number?: string | null;
  npi_number?: string | null;
  facility_name?: string | null;
  city?: string | null;
  state?: string | null;
}): string {
  const id =
    (details.ccn_number || details.npi_number || "").toString().trim() ||
    [
      details.facility_name || "",
      details.city || "",
      details.state || "",
    ]
      .join("|")
      .toLowerCase();
  return id || `tmp-${Date.now()}`;
}

export function addFacilityToCart(
  item: CartFacilityItem,
  existingActiveCount = 0,
  unlockedMulti?: boolean,
  ignoreCap = false,
): CartFacilityItem[] {
  const cart = readFacilityCart();
  if (cart.some((c) => c.cart_key === item.cart_key)) {
    return cart;
  }
  const unlocked =
    typeof unlockedMulti === "boolean"
      ? unlockedMulti
      : existingActiveCount > 0;
  if (
    isSelfServeCartFull(
      existingActiveCount,
      cart.length,
      unlocked,
      ignoreCap,
    )
  ) {
    throw new Error(
      unlocked
        ? `Self-serve facility limit is ${SELF_SERVE_FACILITY_CAP} per account`
        : "Active trial allows only one facility",
    );
  }
  const withFamily: CartFacilityItem = {
    ...item,
    plan_family:
      item.plan_family ||
      planFamilyForFacilityType(
        item.facility_type ||
          (item.facility_details?.facility_type as string | undefined),
      ),
  };
  const next = [...cart, withFamily];
  writeFacilityCart(next);
  return next;
}

export function removeFacilityFromCart(cartKey: string): CartFacilityItem[] {
  const next = readFacilityCart().filter((c) => c.cart_key !== cartKey);
  writeFacilityCart(next);
  return next;
}

/**
 * Catalog plan.price from pricing API (same field Hub Pricing page uses).
 * Do not invent totals from content_package rollups — those can diverge from plan.price.
 */
export function chargeableUnitFromTier(tier: {
  price?: number | string | null;
  starting_price?: number | string | null;
  amount?: number | string | null;
} | null | undefined): number {
  if (!tier) return 0;
  const raw = tier.price ?? tier.starting_price ?? tier.amount ?? 0;
  const n = typeof raw === "string" ? parseFloat(raw) : Number(raw);
  return Number.isFinite(n) ? n : 0;
}

/**
 * Line total for cycle-specific unit prices from the pricing API.
 * Monthly tiers already return monthly unit; yearly tiers already return yearly unit.
 * Do not multiply annual by 12 again.
 */
export function computeOrderLineTotal(
  unitForCycle: number,
  count: number,
  _cycle?: "monthly" | "annual",
): number {
  if (count < 1 || unitForCycle < 0) return 0;
  return unitForCycle * count;
}
